import type { Environment } from "@app/resolveModule/env";
import { DocportalTopMenu } from "@components/HomePage/TopMenu/DocportalTopMenu";
import { TopMenuStyledLogo } from "@components/HomePage/TopMenuLogo";
import { classNames } from "@components/libs/classNames";
import type { Section } from "@core/SitePresenter/SitePresenter";
import IsMacService from "@core-ui/ContextServices/IsMac";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import WorkspaceService from "@core-ui/ContextServices/Workspace";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import styled from "@emotion/styled";
import AddCatalogMenu from "@ext/catalog/actions/AddCatalogMenu";
import useSignOut from "@ext/enterprise/components/SingInOut/hooks/useSignOut";
import { GesCloudSignInOut, SingInBrowser, SingInTauri } from "@ext/enterprise/components/SingInOut/SingInOut";
import SwitchUiLanguage from "@ext/localization/actions/SwitchUiLanguage";
import PermissionService from "@ext/security/logic/Permission/components/PermissionService";
import { editCatalogContentPermission } from "@ext/security/logic/Permission/Permissions";
import Search from "@ext/serach/components/Search";
import ThemeToggle from "@ext/Theme/components/ThemeToggle";
import SwitchWorkspace from "@ext/workspace/components/SwitchWorkspace";
import type React from "react";

const TopMenuWrapper = ({ children, className }: { children: React.ReactNode; className?: string }) => {
	return (
		<div
			className={classNames(`w-full bg-alpha-40 top-0`, {}, [className])}
			data-qa="top-menu"
			data-testid="top-menu"
			role="menubar"
			style={{ backdropFilter: "blur(24px)", position: "sticky", zIndex: "var(--z-index-top-menu)" }}
		>
			<div className="top-menu">
				<div className={`flex flex-row items-center justify-between py-1.5 lg:py-2.5`}>{children}</div>
			</div>
		</div>
	);
};

const TopMenuLeftSideWithLogo = ({ children }: { children?: React.ReactNode }) => {
	return (
		<div className="flex flex-row items-center gap-3 lg:gap-6">
			<div>
				<TopMenuStyledLogo />
			</div>
			<div className="flex flex-row items-center lg:gap-2">{children}</div>
		</div>
	);
};

const TopMenuRightSide = ({ children }: { children: React.ReactNode }) => {
	return <div className="flex flex-row items-center gap-0.5 lg:gap-2">{children}</div>;
};

const TopMenuSwitchUiLanguageButton = () => <SwitchUiLanguage size="lg" />;

const TopMenuSearch = ({ section }: { section?: Section }) => <Search isHomePage section={section} />;

const TopMenuThemeToggle = () => <ThemeToggle isHomePage />;

export const topMenuItemClassName = "hover:bg-alpha-high-97 menu-open";

const components: Record<Environment, (props: { section?: Section }) => React.ReactNode> = {
	tauri: ({ section }) => <TauriEditorTopMenu section={section} />,
	next: ({ section }) => <DocportalTopMenu section={section} />,
	static: () => <StaticTopMenu />,
	browser: ({ section }) => <BrowserEditorTopMenu section={section} />,
	cli: () => <StaticTopMenu />,
	test: () => null,
	docportal: () => null,
};

const TopMenu = ({ section }: { section?: Section }) => {
	const { environment } = usePlatform();
	const { url: gesCloudUrl } = PageDataContextService.value.conf.enterpriseCloud;

	if (environment === "browser" && gesCloudUrl) return <GesCloudTopMenu section={section} />;

	return components[environment]({ section });
};

export default styled(TopMenu)`
	.menu-open[data-state="open"] {
		background-color: hsl(var(--alpha-high-05));
	}
`;

const StaticTopMenu = () => {
	return (
		<TopMenuWrapper>
			<TopMenuLeftSideWithLogo />
			<TopMenuRightSide>
				<SwitchUiLanguage size="lg" />
				<ThemeToggle isHomePage />
			</TopMenuRightSide>
		</TopMenuWrapper>
	);
};

const TauriEditorTopMenu = ({ section }: { section?: Section }) => {
	const isMacDesktop = IsMacService.value;
	const canAddCatalog = PermissionService.useCheckAnyCatalogPermission(editCatalogContentPermission);
	const hasWorkspace = WorkspaceService.hasActive();

	return (
		<TopMenuWrapper className={isMacDesktop ? "pt-4" : ""}>
			<TopMenuLeftSideWithLogo>
				{hasWorkspace && <SwitchWorkspace />}
				{canAddCatalog && <AddCatalogMenu />}
			</TopMenuLeftSideWithLogo>
			<TopMenuRightSide>
				{hasWorkspace && <TopMenuSearch section={section} />}
				<TopMenuSwitchUiLanguageButton />
				<TopMenuThemeToggle />
				<SingInTauri />
			</TopMenuRightSide>
		</TopMenuWrapper>
	);
};

const BrowserEditorTopMenu = ({ section }: { section?: Section }) => {
	const hasWorkspace = WorkspaceService.hasActive();
	const canAddCatalog = PermissionService.useCheckAnyCatalogPermission(editCatalogContentPermission);
	const isEnterprise = !!PageDataContextService.value.conf.activeGesUrl;
	const isLogged = PageDataContextService.value.isLogged;

	let isWorkWithCatalogAllowed = hasWorkspace;
	if (isEnterprise && !isLogged) isWorkWithCatalogAllowed = false;

	return (
		<TopMenuWrapper>
			<TopMenuLeftSideWithLogo>
				{isWorkWithCatalogAllowed && <SwitchWorkspace />}
				{canAddCatalog && isWorkWithCatalogAllowed && <AddCatalogMenu />}
			</TopMenuLeftSideWithLogo>
			<TopMenuRightSide>
				{hasWorkspace && <TopMenuSearch section={section} />}
				<TopMenuSwitchUiLanguageButton />
				<TopMenuThemeToggle />
				{isEnterprise && <SingInBrowser />}
			</TopMenuRightSide>
		</TopMenuWrapper>
	);
};

const GesCloudTopMenu = ({ section }: { section?: Section }) => {
	const hasWorkspace = WorkspaceService.hasActive();
	const { isLogged } = useSignOut();
	const { enabled } = PageDataContextService.value.conf.enterpriseCloud;

	const isWorkWithCatalogAllowed = hasWorkspace && (isLogged || !enabled);

	return (
		<TopMenuWrapper>
			<TopMenuLeftSideWithLogo>
				{isWorkWithCatalogAllowed && <SwitchWorkspace />}
				{isWorkWithCatalogAllowed && <AddCatalogMenu />}
			</TopMenuLeftSideWithLogo>
			<TopMenuRightSide>
				{isWorkWithCatalogAllowed && <TopMenuSearch section={section} />}
				<TopMenuSwitchUiLanguageButton />
				<TopMenuThemeToggle />
				<GesCloudSignInOut />
			</TopMenuRightSide>
		</TopMenuWrapper>
	);
};
